export const TIMER_STATES = Object.freeze({
  IDLE: 'idle',
  RUNNING: 'running',
  PAUSED: 'paused',
  STOPPED: 'stopped',
});

// 分钟到达 99 后保持两位显示，让秒位继续走到电子屏的字面上限。
export const MAX_TIMER_MS = ((99 * 60) + 99) * 1000 + 999;

export function splitTimerTime(elapsedMs) {
  const clamped = Math.min(Math.max(Math.floor(Number(elapsedMs) || 0), 0), MAX_TIMER_MS);
  const minuteBoundary = 99 * 60 * 1000;
  const minutes = Math.min(Math.floor(clamped / 60_000), 99);
  const seconds = minutes < 99
    ? Math.floor(clamped / 1000) % 60
    : Math.floor((clamped - minuteBoundary) / 1000);

  return {
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
    milliseconds: String(clamped % 1000).padStart(3, '0'),
  };
}

export function formatTimerTime(elapsedMs) {
  const { minutes, seconds, milliseconds } = splitTimerTime(elapsedMs);
  return `${minutes}:${seconds}.${milliseconds}`;
}

export class PracticeTimer {
  constructor({
    now = () => performance.now(),
    requestFrame = (callback) => requestAnimationFrame(callback),
    cancelFrame = (frameId) => cancelAnimationFrame(frameId),
    onTick = (_elapsedMs) => {},
    onStateChange = (_state, _reason) => {},
  } = {}) {
    this.now = now;
    this.requestFrame = requestFrame;
    this.cancelFrame = cancelFrame;
    this.onTick = onTick;
    this.onStateChange = onStateChange;
    /** @type {'idle' | 'running' | 'paused' | 'stopped'} */
    this.state = TIMER_STATES.IDLE;
    this.elapsedMs = 0;
    this.startedAtMs = null;
    this.frameId = null;
    this.frame = () => this.#updateFrame();
  }

  getElapsedMs() {
    if (this.state !== TIMER_STATES.RUNNING || this.startedAtMs === null) return this.elapsedMs;
    return Math.min(this.elapsedMs + Math.max(this.now() - this.startedAtMs, 0), MAX_TIMER_MS);
  }

  start() {
    if (this.state !== TIMER_STATES.IDLE && this.state !== TIMER_STATES.PAUSED) return false;
    this.startedAtMs = this.now();
    this.state = TIMER_STATES.RUNNING;
    this.onStateChange(this.state, 'start');
    this.onTick(this.getElapsedMs());
    this.#scheduleFrame();
    return true;
  }

  pause() {
    if (this.state !== TIMER_STATES.RUNNING) return false;
    this.#captureElapsed();
    this.#cancelFrame();
    this.state = TIMER_STATES.PAUSED;
    this.onTick(this.elapsedMs);
    this.onStateChange(this.state, 'pause');
    return true;
  }

  stop(reason = 'stop') {
    if (this.state !== TIMER_STATES.RUNNING && this.state !== TIMER_STATES.PAUSED) return false;
    if (this.state === TIMER_STATES.RUNNING) this.#captureElapsed();
    this.#cancelFrame();
    this.state = TIMER_STATES.STOPPED;
    this.onTick(this.elapsedMs);
    this.onStateChange(this.state, reason);
    return true;
  }

  reset() {
    this.#cancelFrame();
    this.elapsedMs = 0;
    this.startedAtMs = null;
    this.state = TIMER_STATES.IDLE;
    this.onTick(0);
    this.onStateChange(this.state, 'reset');
    return true;
  }

  dispose() {
    this.#cancelFrame();
  }

  #captureElapsed() {
    this.elapsedMs = this.getElapsedMs();
    this.startedAtMs = null;
  }

  #scheduleFrame() {
    if (this.frameId === null) this.frameId = this.requestFrame(this.frame);
  }

  #cancelFrame() {
    if (this.frameId !== null) this.cancelFrame(this.frameId);
    this.frameId = null;
  }

  #updateFrame() {
    this.frameId = null;
    if (this.state !== TIMER_STATES.RUNNING) return;
    const elapsedMs = this.getElapsedMs();
    this.onTick(elapsedMs);
    if (elapsedMs >= MAX_TIMER_MS) {
      this.elapsedMs = MAX_TIMER_MS;
      this.startedAtMs = null;
      this.state = TIMER_STATES.STOPPED;
      this.onStateChange(this.state, 'maximum');
      return;
    }
    this.#scheduleFrame();
  }
}
